<template>
    <DCard>
        <section class="checks-table">
            <div class="table-header">
                <div>
                    <DButton
                        mini
                        :icon="collapsedGroups.length > 0 ? 'arrow-down' : 'arrow-up'"
                        @click="toggleAll()"
                    />
                </div>
                <div>{{ __('orders.check.manual_checks_tab.id') }}</div>
                <div>{{ __('orders.check.manual_checks_tab.name') }}</div>
                <div />
                <div>{{ __('orders.check.manual_checks_tab.result') }}</div>
                <div />
                <div />
                <div />
                <div>{{ __('orders.check.manual_checks_tab.text') }}</div>
                <div />
                <div />
            </div>
            <main>
                <div
                    v-for="(item, index) in manualCheckGroupsData"
                    :key="index"
                    class="item-group"
                >
                    <div class="item-header">
                        <div>
                            <DButton
                                :id="`toggle_group_${index}`"
                                mini
                                :icon="collapsedGroups.includes(index) ? 'arrow-down' : 'arrow-up'"
                                @click="toggleGroup(index)"
                            />
                        </div>
                        <div>
                            {{ item.name }}
                        </div>
                        <div />
                        <div>
                            {{ __('orders.check.manual_checks_tab.ok') }}
                        </div>
                        <div>
                            {{ __('orders.check.manual_checks_tab.unknown') }}
                        </div>
                        <div>
                            {{ __('orders.check.manual_checks_tab.nok') }}
                        </div>
                        <div />
                        <div>
                            {{ __('orders.check.manual_checks_tab.solved') }}
                        </div>
                        <div />
                        <div />
                        <div />
                    </div>
                    <div
                        v-for="(check, indexCheck) in item.order_manual_checks"
                        v-show="!collapsedGroups.includes(index)"
                        :key="indexCheck"
                        class="item"
                    >
                        <div />
                        <div>{{ check.id }}</div>
                        <div>
                            {{ check.name }}
                        </div>
                        <div>
                            <DFormCheck
                                :id="`ok_${check.id}`"
                                v-model="check.status_ok"
                                :disabled="buttonsDisabled"
                                inline
                                type="radio"
                                :name="check.id"
                                :checked="check.status_ok"
                                @change="setRadioButtons(check, 'status_ok')"
                            />
                        </div>
                        <div>
                            <DFormCheck
                                :id="`unknown_${check.id}`"
                                v-model="check.status_unknown"
                                :disabled="buttonsDisabled"
                                inline
                                type="radio"
                                :name="check.id"
                                :checked="check.status_unknown"
                                @change="setRadioButtons(check, 'status_unknown')"
                            />
                        </div>
                        <div>
                            <DFormCheck
                                :id="`nok_${check.id}`"
                                v-model="check.status_nok"
                                :disabled="buttonsDisabled"
                                inline
                                type="radio"
                                :name="check.id"
                                :checked="check.status_nok"
                                @change="setRadioButtons(check, 'status_nok')"
                            />
                        </div>
                        <div class="comment">
                            <DIcon
                                v-if="userPermissions.includes('order.view')"
                                :id="`dialog_for_clarification_${check.check_id}`"
                                name="chat-processing-outline"
                                :size="30"
                                :class="hasClarificationComment(check) ? 'text-warning' : ''"
                                @click="openClarificationModal(check)"
                            />
                        </div>
                        <div>
                            <DFormCheck
                                :id="`solved_${check.id}`"
                                v-model="check.solved"
                                :disabled="buttonsDisabled"
                            />
                        </div>
                        <div>
                            {{ check.text }}
                        </div>
                        <div class="buttons">
                            <DIcon
                                v-if="userPermissions.includes('order.view')"
                                :id="`info_${check.id}`"
                                v-c-tooltip="check.tool_tip ?? ''"
                                name="information-slab-circle-outline"
                                :size="30"
                            />
                        </div>
                    </div>
                </div>
            </main>
        </section>
        <ClarificationModal
            v-if="showClarificationModal"
            :show-modal="showClarificationModal"
            :order="data"
            :item="selectedItem"
            :check-id="selectedItem.check_id"
            :setting-check-id="selectedItem.id"
            :editable="false"
            @close-modal="closeClarificationModal"
        />
        <DFabs>
            <CheckButtons>
                <template #additional_buttons>
                    <DFab
                        v-if="data.status === 'screening' && userPermissions.includes('order.reject')"
                        id="btn-reject"
                        :disabled="isInLoadingState"
                        icon="close-circle"
                        :label="__('orders.check.manual_checks_tab.reject')"
                        @click="showRejectHistoryModal()"
                    />
                    <DFab
                        v-if="data.status === 'rejected' && userPermissions.includes('order.reject')"
                        id="btn-reopen"
                        :disabled="isInLoadingState"
                        icon="replay"
                        :label="__('orders.check.manual_checks_tab.reopen')"
                        @click="showScreeningHistoryModal()"
                    />
                    <DFab
                        v-if="(data.status === 'screening' || data.status === '4_eyes_check') && userPermissions.includes('order.accept')"
                        id="btn-save"
                        :disabled="isInLoadingState"
                        icon="content-save"
                        :label="__('orders.check.save')"
                        @click="save()"
                    />
                    <DFab
                        v-if="data.status === 'screening' && data.clientCustomerId !== null && userPermissions.includes('order.accept')"
                        id="btn-accept"
                        :disabled="isInLoadingState"
                        icon="check-circle"
                        :label="__('orders.check.manual_checks_tab.accept')"
                        @click="openAcceptModal()"
                    />
                    <DDropdown
                        v-if="(data.status === 'screening' || data.status === '4_eyes_check') && userPermissions.includes('order.accept')"
                        id="btn-dots"
                        color="primary"
                        add-menu-classes="pt-0 pb-0"
                        icon="dots-horizontal"
                        :size="'sm'"
                    >
                        <DDropdownItem
                            v-if="data.status === '4_eyes_check'"
                            id="back-to-screening"
                            @click="showBackToScreeningModal()"
                        >
                            <div class="row pl-2 pr-1">
                                <div>
                                    {{ __('orders.check.manual_checks_tab.back_to_screening') }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="isScreeningNOkExternalResult === true && isSolved === false"
                            id="clarify-external"
                            @click="showClarifyExternalModal()"
                        >
                            <div class="row pl-2 pr-1">
                                <div>
                                    {{ __('orders.check.manual_checks_tab.clarify_external') }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="isScreeningNOkInternalResult === true && isSolved === false"
                            id="clarify-internal"
                            @click="showClarifyInternalModal()"
                        >
                            <div class="row pl-2 pr-1">
                                <div>
                                    {{ __('orders.check.manual_checks_tab.clarify_internal') }}
                                </div>
                            </div>
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="is4EyeResult && data.status === 'screening' && isSolved === false"
                            id="4-eyes"
                            @click="showFourEyesModal()"
                        >
                            <div class="row pl-2 pr-1">
                                <div>
                                    {{ __('orders.check.manual_checks_tab.4_eyes') }}
                                </div>
                            </div>
                        </DDropdownItem>
                    </DDropdown>
                </template>
            </CheckButtons>
        </DFabs>
        <OrderCommentModal
            v-if="showOrderCommentModal"
            :action="action"
            :title="orderCommentModalTitle"
            :order="data"
            :comment-required="commentRequired"
            :show-modal="showOrderCommentModal"
            @close-modal="closeCommentModal"
        />
        <PsaAcceptModal
            v-if="showAcceptModal"
            :order="data"
            :show-modal="showAcceptModal"
            @close-modal="closeAcceptModal"
        />
    </DCard>
</template>

<script>
import OrderBase from '~@/Pages/Orders/Psa/Checks/Base'
import App from '~@/App.vue'
import CheckButtons from '~@/Components/Orders/CheckButtons'
import OrderCommentModal from '~@/Components/Orders/Psa/OrderCommentModal'
import ClarificationModal from '~@/Components/Orders/Psa/ClarificationChatModal.vue'
import PsaAcceptModal from '~@/Components/Orders/Psa/PsaAcceptModal'

export default {
    name: 'ManualChecks',
    components: { PsaAcceptModal, ClarificationModal, OrderCommentModal, CheckButtons },
    layout: [App, OrderBase],
    props: {
        data: {
            type: Object,
            default: () => { return {} }
        },
        manualCheckGroups: {
            type: Array,
            required: false,
            default: () => { return [] }
        },
        orderAdditionalInfo: {
            type: Object,
            default: () => { return {} }
        }
    },
    data () {
        return {
            manualCheckGroupsData: this.manualCheckGroups,
            showOrderCommentModal: false,
            orderCommentModalTitle: false,
            action: null,
            commentRequired: true,
            showOrderHistoryModal: false,
            orderHistoryModalTitle: false,
            status: null,
            collapsedGroups: [],
            showClarificationModal: false,
            selectedItem: null,
            showAcceptModal: false
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        isSolved () {
            let statusSolved = true

            // Status is solved only if all statuses ok are checked or all statuses solved are checked
            this.manualCheckGroupsData.forEach((manualCheckGroup) => {
                manualCheckGroup.order_manual_checks.forEach((manualCheck) => {
                    if (manualCheck.solved === false) {
                        statusSolved = false
                    }
                })
            })

            return statusSolved
        },
        isScreeningNOkInternalResult () {
            let externalFailed = false

            this.manualCheckGroupsData.forEach((manualCheckGroup) => {
                manualCheckGroup.order_manual_checks.forEach((manualCheck) => {
                    if (manualCheck.status_nok === true) {
                        externalFailed = true
                    }
                })
            })

            return externalFailed === true && (this.orderAdditionalInfo.screening_result === 'screening_result_nok_internal' ||
                this.orderAdditionalInfo.screening_result === 'screening_result_nok_external_and_internal')
        },
        isScreeningNOkExternalResult () {
            let externalFailed = false

            this.manualCheckGroupsData.forEach((manualCheckGroup) => {
                manualCheckGroup.order_manual_checks.forEach((manualCheck) => {
                    if (manualCheck.status_nok === true) {
                        externalFailed = true
                    }
                })
            })

            return externalFailed === true && (this.orderAdditionalInfo.screening_result === 'screening_result_nok_external' ||
                this.orderAdditionalInfo.screening_result === 'screening_result_nok_external_and_internal')
        },
        is4EyeResult () {
            return this.orderAdditionalInfo.screening_result === null || this.orderAdditionalInfo.screening_result === 'screening_result_nok_external' ||
                this.orderAdditionalInfo.screening_result === 'screening_result_nok_internal' ||
                this.orderAdditionalInfo.screening_result === 'screening_result_nok_external_and_internal' ||
                this.orderAdditionalInfo.screening_result === 'screening_result_faulty'
        },
        buttonsDisabled () {
            return (this.data.status !== 'screening' && this.data.status !== '4_eyes_check') || this.userPermissions.includes('order.accept') === false
        }
    },
    watch: {
        manualCheckGroups: {
            handler (value) {
                this.manualCheckGroupsData = value
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        showRejectHistoryModal () {
            this.showOrderCommentModal = true
            this.commentRequired = true
            this.action = 'reject'
            this.orderCommentModalTitle = this.__('orders.check.manual_checks_tab.reject_order_dialog_title')
        },
        showScreeningHistoryModal () {
            this.showOrderCommentModal = true
            this.commentRequired = true
            this.action = 'reopen'
            this.orderCommentModalTitle = this.__('orders.check.manual_checks_tab.reopen_order_dialog_title')
        },
        showBackToScreeningModal () {
            this.showOrderCommentModal = true
            this.commentRequired = false
            this.action = 'back_to_screening'
            this.orderCommentModalTitle = this.__('orders.check.manual_checks_tab.back_to_screening')
        },
        showClarifyExternalModal () {
            this.showOrderCommentModal = true
            this.commentRequired = false
            this.action = 'clarify_external'
            this.orderCommentModalTitle = this.__('orders.check.manual_checks_tab.clarify_external')
        },
        showClarifyInternalModal () {
            this.showOrderCommentModal = true
            this.commentRequired = false
            this.action = 'clarify_internal'
            this.orderCommentModalTitle = this.__('orders.check.manual_checks_tab.clarify_internal')
        },
        showFourEyesModal () {
            this.showOrderCommentModal = true
            this.commentRequired = false
            this.action = '4_eyes'
            this.orderCommentModalTitle = this.__('orders.check.manual_checks_tab.4_eyes')
        },
        closeCommentModal () {
            this.showOrderCommentModal = false
            this.action = null
            this.orderCommentModalTitle = null
        },
        toggleGroup (groupId) {
            if (this.collapsedGroups.includes(groupId)) {
                this.collapsedGroups = this.collapsedGroups.filter(e => e !== groupId)
            } else {
                this.collapsedGroups.push(groupId)
            }
        },
        toggleAll () {
            if (this.collapsedGroups.length > 0) {
                this.collapsedGroups = []
            } else {
                this.manualCheckGroups.forEach((manualCheckGroup, index) => {
                    if (!this.collapsedGroups.includes(index)) {
                        this.collapsedGroups.push(index)
                    }
                })
            }
        },
        setRadioButtons (check, status) {
            this.manualCheckGroupsData.forEach((manualCheckGroup) => {
                manualCheckGroup.order_manual_checks.forEach((manualCheck) => {
                    if (manualCheck.id === check.id) {
                        // Only one radio button can be true
                        manualCheck.status_ok = false
                        manualCheck.status_unknown = false
                        manualCheck.status_nok = false
                        manualCheck[status] = true
                    }
                })
            })
        },
        save () {
            this.$inertia.post(
                this.$route('orders.psa.save-manual-checks', {
                    order: this.data.orderData.id
                }),
                this.$inertia.form({ manual_checks: this.flattenChecks() })
            )
        },
        flattenChecks () {
            const result = []

            this.manualCheckGroupsData.forEach((manualCheckGroup) => {
                manualCheckGroup.order_manual_checks.forEach((manualCheck) => {
                    result.push(manualCheck)
                })
            })

            return result
        },
        closeClarificationModal () {
            this.showClarificationModal = false
            this.manualCheckGroupsData = this.manualCheckGroups
            this.selectedItem = null
        },
        openClarificationModal (item) {
            this.showClarificationModal = true
            this.selectedItem = item
        },
        closeAcceptModal () {
            this.showAcceptModal = false
        },
        openAcceptModal () {
            this.showAcceptModal = true
        },
        hasClarificationComment (check) {
            return check.comments.filter(comment => comment.is_clarification === true).length > 0
        }
    }
}
</script>

<style lang="scss">
.checks-table {
    overflow-x: auto;

    & .table-header {
        border-bottom: 1px solid rgba(0,0,0,0.14);
        display: grid;
        grid-template-columns: 45px 220px 130px 40px 40px 40px 40px 80px 1fr 30px 30px;
        grid-gap: 10px;
        align-items: center;

        & > div {
            padding: 10px;
            font-weight: bold;
        }
    }
    & main {
        & .item-group {
            border-bottom: 1px solid rgba(0,0,0,0.3);
            position: relative;

            & .item-header {
                display: grid;
                grid-template-columns: 45px 220px 130px 40px 40px 40px 40px 80px 1fr 30px 30px;
                grid-gap: 10px;
                background: #fff;
                border-bottom: 1px solid rgba(0,0,0,0.14);
                position: sticky;
                top: -25px;
                width: 100%;
                z-index: 100;

                & > div {
                    padding: 10px;
                    align-self: center;
                    font-weight: bold;
                }
            }
            & .item {
                white-space: break-spaces;
                display: grid;
                grid-template-columns: 45px 220px 130px 40px 40px 40px 40px 80px 1fr 30px 30px;
                grid-gap: 10px;

                & > div {
                    padding: 10px;
                    align-self: center;
                }
                & > div:nth-child(1) {
                    position: relative;
                    overflow: hidden;
                    padding: 0;
                    height: 100%;

                    &::after {
                        content: "";
                        display: block;
                        position: absolute;
                        left: 29px;
                        top: 0px;
                        width: 2px;
                        height: 60px;
                        background: rgba(0,0,0,0.08);
                    }
                    &::before {
                        content: "";
                        display: block;
                        position: absolute;
                        left: 31px;
                        top: 25px;
                        width: 10px;
                        border-top-right-radius: 4px;
                        border-bottom-right-radius: 4px;
                        height: 2px;
                        background: rgba(0,0,0,0.08);
                    }
                }
                &:last-of-type > div:nth-child(1)::after {
                    height: 35px;
                    background: rgba(0,0,0,0.07);
                    border-bottom-left-radius: 4px;
                    border-bottom-right-radius: 4px;
                }

                &:hover {
                    background: rgba(0,0,0,0.07);
                }
            }
        }
    }
}
.buttons {
    display: flex;
    grid-gap: 10px;
}
.btn-group button {
    border-radius: 100px !important;
}
.dropdown-toggle::after {
    display: none !important;
}
.d-fabs {
    align-items: unset !important;
}
.comment {
    cursor: pointer;
}
</style>
